import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import SearchIcon from "@mui/icons-material/Search";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import useInfiniteFeed from "hooks/useInfiniteFeed";
import {
  CreateQuestionCall,
  GetCommunityFeedPageCall,
} from "../../redux/api/question.api";
import { GetPublicCall } from "../../redux/api/public.api";
import { useSelector } from "react-redux";
import CommunityFeed, { getFeedKey } from "./CommunityFeed";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 350;
const COMMUNITY_ADS_PUBLIC_KEY = "communityShowAds";

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function CommunityPage() {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const [tab, setTab] = useState("community");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);
  const [askOpen, setAskOpen] = useState(false);
  const [askBody, setAskBody] = useState("");
  const [askFile, setAskFile] = useState(null);
  const [showPromotions, setShowPromotions] = useState(() => {
    const raw = String(localStorage.getItem("communityShowAds") ?? "true").toLowerCase();
    return raw === "true" || raw === "1" || raw === "yes";
  });
  const me = useSelector((s) => s?.auth || {});
  const universityId =
    me?.university_id ?? me?.universityInfo?.id ?? me?.universityInfo?.university_id ?? null;
  const hasUniversityMembership = Boolean(universityId);
  const isPlatformAdmin = String(me?.role || "").toLowerCase() === "admin";
  const universityName = useMemo(
    () =>
      String(
        me?.universityInfo?.name ||
          me?.universityInfo?.short_name ||
          me?.university ||
          "University"
      ).trim() || "University",
    [me?.university, me?.universityInfo?.name, me?.universityInfo?.short_name]
  );
  const universityTabLabel = `${universityName} Private Section`;
  const isUniversityTab = tab === "university";

  useEffect(() => {
    if (!hasUniversityMembership && tab === "university") {
      setTab("community");
    }
  }, [hasUniversityMembership, tab]);

  // Server-side ads preference; changing it resets the feed through `feedKey`.
  useEffect(() => {
    GetPublicCall()
      .then((publicRes) => {
        const rows = Array.isArray(publicRes?.data) ? publicRes.data : [];
        const prefRow = rows.find((r) => r?.key === COMMUNITY_ADS_PUBLIC_KEY);
        if (!prefRow) return;
        const raw = String(prefRow?.data ?? "").toLowerCase();
        const next = raw === "true" || raw === "1" || raw === "yes";
        localStorage.setItem("communityShowAds", String(next));
        setShowPromotions(next);
      })
      .catch(() => {
        // Keep local preference when public settings cannot be fetched.
      });
  }, []);

  useEffect(() => {
    const onPreferenceChanged = (evt) => {
      const nextVal = Boolean(evt?.detail?.showAds);
      setShowPromotions(nextVal);
    };
    window.addEventListener("communityAdsPreferenceChanged", onPreferenceChanged);
    return () =>
      window.removeEventListener("communityAdsPreferenceChanged", onPreferenceChanged);
  }, []);

  const showAds = !isUniversityTab && showPromotions;
  const fetchPage = useCallback(
    async ({ cursor, page }) => {
      const res = await GetCommunityFeedPageCall({
        cursor,
        page,
        limit: PAGE_SIZE,
        ads: showAds,
        search: debouncedSearch,
        ...(isUniversityTab
          ? {
              audience: "university",
              ...(isPlatformAdmin && universityId ? { university_id: universityId } : {}),
            }
          : {}),
      });
      // Ads repeat on every page, so give each placement its own key; strip them if disabled.
      const items = res.items
        .filter((row) => showAds || !row?.ad)
        .map((row) => (row?.ad ? { ...row, _feedKey: `ad:${row.id}:${page}` } : row));
      return { ...res, items };
    },
    [showAds, debouncedSearch, isUniversityTab, isPlatformAdmin, universityId]
  );

  const feedKey = `${tab}|${showAds}|${debouncedSearch}|${universityId}`;
  const feed = useInfiniteFeed({ fetchPage, getKey: getFeedKey, resetKey: feedKey });
  const isInitialLoad = feed.loading && feed.items.length === 0;

  // Column count for the masonry grid.
  const masonryCols = isLgUp ? 3 : isMdUp ? 2 : 1;

  const handleAsk = async () => {
    if (!askBody.trim()) return;
    const res = await CreateQuestionCall({
      body: askBody.trim(),
      file: askFile || undefined,
      community_scope: isUniversityTab ? "university" : "public",
      audience: isUniversityTab ? "university" : "public",
      ...(isUniversityTab && isPlatformAdmin && universityId
        ? { university_id: universityId }
        : {}),
    });
    if (res?.status === 200 || res?.status === 201) {
      setAskOpen(false);
      setAskBody("");
      setAskFile(null);
      feed.reset();
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box pt={2} pb={2}>
        <Card
          sx={{
            borderRadius: 3,
            p: 2,
            backgroundImage: "none",
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "#243049" : theme.palette.background.paper,
            border: (theme) =>
              theme.palette.mode === "dark"
                ? "1px solid rgba(148, 163, 184, 0.38)"
                : "1px solid transparent",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 0 0 1px rgba(255,255,255,0.06), 0 12px 32px rgba(0,0,0,0.55)"
                : undefined,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" fontWeight={700}>
              Community
            </Typography>
            <MDButton color="info" onClick={() => setAskOpen(true)} startIcon={<AddIcon />}>
              Ask
            </MDButton>
          </Stack>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
            <Tab value="community" label="Community" />
            {hasUniversityMembership ? (
              <Tab value="university" label={universityTabLabel} />
            ) : null}
          </Tabs>

          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search questions"
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {isInitialLoad ? (
            <Box py={5} textAlign="center">
              <CircularProgress size={28} />
            </Box>
          ) : !feed.loading && !feed.error && feed.items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {isUniversityTab
                ? `No posts yet in ${universityName}. Be the first to ask.`
                : "No items found."}
            </Typography>
          ) : (
            <CommunityFeed
              items={feed.items}
              columnCount={masonryCols}
              loading={feed.loading}
              error={feed.error}
              hasMore={feed.hasMore}
              sentinelRef={feed.sentinelRef}
              onRetry={feed.loadMore}
            />
          )}
        </Card>
      </Box>

      <Dialog open={askOpen} onClose={() => setAskOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {isUniversityTab ? `Ask ${universityName}` : "Ask Community"}
        </DialogTitle>
        <DialogContent>
          <TextField
            multiline
            minRows={4}
            fullWidth
            autoFocus
            value={askBody}
            onChange={(e) => setAskBody(e.target.value)}
            placeholder="Write your question..."
            sx={{ mb: 2 }}
          />
          <MDButton
            variant="outlined"
            color="info"
            component="label"
            startIcon={<InsertDriveFileIcon />}
          >
            {askFile ? askFile.name : "Attach image/video (optional)"}
            <input
              hidden
              type="file"
              accept="image/*,video/*"
              onChange={(e) => setAskFile(e.target.files?.[0] || null)}
            />
          </MDButton>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={() => setAskOpen(false)}>
            Cancel
          </MDButton>
          <MDButton color="info" onClick={handleAsk} disabled={!askBody.trim()}>
            Post
          </MDButton>
        </DialogActions>
      </Dialog>

    </DashboardLayout>
  );
}

export default CommunityPage;
